/**
 * @author        woody@zumper.com (Woody Anderson), yewon@zumper.com(Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   fetch() wrapping Factory. produces a 'fetch' function that retries on 434 etc.
 */

import { getAbsoluteUrl, timeoutAsync } from '@zcode/nr-utils'
import { ONE_SECOND, isCypress } from '@zcode/utils'
import QuickLRU from 'quick-lru'
import { logger } from '../../logger'
import { ON_BROWSER } from '../env'
import * as TokenManager from './token_manager'

const RATE_LIMIT_MS = ONE_SECOND
const RATE_LIMIT_COUNT = 50
const INCLUDE_RETRY_AFTER_ENDPOINTS = [/\/unread/]

const fetchReject = (opts, response, data) =>
  // eslint-disable-next-line prefer-promise-reject-errors
  Promise.reject({
    data,
    config: opts,
    headers: response.headers,
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  })

const timeoutCalc = (retryLimit) => {
  if (!retryLimit) {
    return 200
  }
  if (retryLimit === 1) {
    return 2000
  }
}

/**
 * fetch() wrapping Factory. produces a 'fetch' function that retries on 434 etc.
 * converts raw api responses to Bizos etc, handles arrays etc.
 *
 * @param {typeof fetch} gfetch    defaults to 'global fetch()'
 * @returns {Function}  used as fetch(options)
 */
// eslint-disable-next-line import/no-anonymous-default-export,import/no-default-export
export default (gfetch) => {
  const headers = { 'Content-Type': 'application/json' }
  const fetchMap = new QuickLRU({ maxSize: 1000 })
  let redirectLoopTriggered = false

  const fetchit = async (options) => {
    if (redirectLoopTriggered) {
      await new Promise((resolve) => {
        setTimeout(resolve, 1000)
      })
      return null
    }
    const { Bizo, serverRetry, tokenRetry, url } = options
    const { xzToken, csrfToken } = TokenManager.get()
    const absUrl = getAbsoluteUrl(url)

    if (ON_BROWSER) {
      const now = Date.now()
      const fetchData = fetchMap.get(absUrl)
      if (fetchData) {
        if (now > fetchData.lastFetchTime + RATE_LIMIT_MS) {
          fetchData.lastFetchTime = now
          fetchData.fetchCount = 1
        } else {
          fetchData.lastFetchTime = now
          fetchData.fetchCount += 1
          if (fetchData.fetchCount > RATE_LIMIT_COUNT && !isCypress()) {
            const message = 'api infinite loop protection triggered'
            logger.error(message, absUrl, new Error(message))
            redirectLoopTriggered = true
            window.location.href = '/'
            await new Promise((resolve) => {
              setTimeout(resolve, 1000)
            })
            return null
          }
        }
      } else {
        fetchMap.set(absUrl, {
          lastFetchTime: now,
          fetchCount: 1,
        })
      }
    }

    const opts = {
      credentials: 'same-origin',
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
      url: absUrl,
    }
    if (xzToken) {
      opts.headers['X-Zumper-XZ-Token'] = xzToken
    }
    if (csrfToken) {
      opts.headers['X-CSRFToken'] = csrfToken
    }

    /** @type Response */
    let response

    try {
      const fetx = gfetch || fetch
      response = await fetx(opts.url, opts)
    } catch (e) {
      logger.error('fetch failed', e)
      throw e
    }
    if (response.status >= 500) {
      // retry for these incase server has a hiccup
      if (serverRetry !== 0) {
        const delay = timeoutCalc(serverRetry)
        opts.serverRetry = opts.serverRetry ? opts.serverRetry - 1 : 1
        await timeoutAsync(delay)
        return fetchit(opts)
      }
      return fetchReject(opts, response)
    }
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      try {
        const data = await response.json()
        if (response.ok) {
          if (Bizo) {
            const factory = Bizo.fromJS ? (d) => Bizo.fromJS(d) : (d) => new Bizo(d)
            return Array.isArray(data) ? data.map(factory) : factory(data)
          }
          if (INCLUDE_RETRY_AFTER_ENDPOINTS.some((r) => r.test(opts.url))) {
            const retryAfter = response.headers.get('Retry-After')
              ? Number(response.headers.get('Retry-After'))
              : null
            return {
              ...(data ? (typeof data === 'object' ? { ...data } : { data }) : {}),
              ...(retryAfter != null && { retryAfter }),
            }
          }
          return data
        }
        if (response.status === 434 || response.status === 403) {
          // re-fetchit will automatically re-mixin fresh xz token
          TokenManager.set({ xzToken: data.xz_token, csrfToken: data.csrf })
          if (tokenRetry !== 0) {
            // by default, limit to 3 attempts: '1' means 3. the first, this one, and the next '1'.
            opts.tokenRetry = opts.tokenRetry ? opts.tokenRetry - 1 : 1
            return fetchit(opts)
          }
        }
        // some other http status code, reject
        return fetchReject(opts, response, data)
      } catch (e) {
        logger.error('failed to process json response', e)
        return fetchReject(opts, response)
      }
    }
    return fetchReject(opts, response)
  }
  return fetchit
}
